import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import AbiBillingApiUrls from '../../../apiUrls/AbiBillingApiUrls';
import Spinner from '../../../components/era-package/era/Spinner';
import TextField from '../../../components/era-package/era/DebouncedTextField';
import Button from '../../../components/era-package/forms/Button';
import { billingRecord } from '../fields/billing-record';

const BillingRecordList = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchData = useCallback(async (pageIndex, pageSize, search) => {
    try {
      setIsRefreshing(true);

      const response = await fetch(AbiBillingApiUrls.GET, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
        },
        body: JSON.stringify({
          page: pageIndex,
          size: pageSize,
          ...(search && { search }),
        }),
      });
      const json = await response.json();
      setData(json.data?.content || json.content || []);
      setTotalElements(json.data?.totalElements || json.totalElements || 0);
    } catch (error) {
      console.error('Error fetching billing records:', error);
      setData([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pagination.pageIndex, pagination.pageSize, globalFilter);
  }, [fetchData, pagination.pageIndex, pagination.pageSize, globalFilter]);

  const handleRefresh = useCallback(async () => {
    await fetchData(pagination.pageIndex, pagination.pageSize, globalFilter);
  }, [fetchData, pagination.pageIndex, pagination.pageSize, globalFilter]);

  const columns = useMemo(
    () =>
      billingRecord.map((field) => ({
        accessorKey: field.name,
        header: field.label,
        cell: (info) => {
          const value = info.getValue();
          return (value === null || value === undefined || value === '') ? '-' : value;
        },
      })),
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalElements / pagination.pageSize),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (loading) {
    return (
      <div style={{ position: 'relative', height: '200px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        width="100%"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            placeholder="Search all columns..."
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} width="14px" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 500 },
            }}
          />
          <Button
            startIcon={<FontAwesomeIcon icon={faRefresh} width="15" />}
            variant="outlined"
            onClick={handleRefresh}
            disabled={isRefreshing}
            size="medium"
            title="Retrieve latest billing records"
            color="success"
          >
            Refresh
          </Button>
          {isRefreshing && <CircularProgress size={20} />}
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: header.column.getCanSort()
                          ? theme.palette.action.hover
                          : 'transparent',
                      },
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                          {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} hover>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ whiteSpace: 'nowrap' }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color={theme.palette.info.main}>
                    No billing records found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalElements > 0 && (
        <Box
          width="100%"
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignSelf="flex-end"
          justifyContent="space-between"
          alignItems="center"
          gap={{ xs: 0, md: 2 }}
        >
          <Typography variant="h6">
            Total {totalElements} Records
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalElements}
            rowsPerPage={pagination.pageSize}
            page={pagination.pageIndex}
            onPageChange={(event, newPage) => {
              setPagination((prev) => ({ ...prev, pageIndex: newPage }));
            }}
            onRowsPerPageChange={(event) => {
              setPagination((prev) => ({
                ...prev,
                pageSize: parseInt(event.target.value, 10),
                pageIndex: 0,
              }));
            }}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ mt: '0!important' }}
          />
        </Box>
      )}
    </Stack>
  );
};

export default BillingRecordList;
